import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import api from '../api';
import {
    Box,
    Container,
    Grid,
    Paper,
    Typography,
    TextField,
    Button,
    CircularProgress,
    Link,
    InputAdornment,
    IconButton,
    Divider
} from '@mui/material';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import ComplisenseLogo from '../Components/ComplisenseLogo';

// --- Reused styles (avoid recreating objects every render) ---

const rootBackgroundSx = {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    bgcolor: 'background.default',
    backgroundImage: `
    radial-gradient(1200px 600px at 10% -10%, rgba(99,102,241,0.12), transparent),
    radial-gradient(800px 500px at 100% 0%, rgba(34,211,238,0.14), transparent),
    linear-gradient(180deg, rgba(2,6,23,0.02), rgba(2,6,23,0.02))
  `,
    p: { xs: 2, md: 4 }
};

const heroPaperSx = {
    height: '100%',
    borderRadius: 4,
    p: 5,
    color: 'common.white',
    overflow: 'hidden',
    position: 'relative',
    background: 'linear-gradient(135deg, #6366F1 0%, #22D3EE 100%)'
};

const heroOverlaySx = {
    position: 'absolute',
    inset: 0,
    opacity: 0.15,
    backgroundImage: 'radial-gradient(#fff 1px, transparent 1px)',
    backgroundSize: '16px 16px'
};

const authPaperSx = {
    height: '100%',
    borderRadius: 4,
    p: { xs: 3, sm: 5 },
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    backdropFilter: 'blur(8px)',
    backgroundColor: 'rgba(255,255,255,0.86)'
};

const textFieldSx = {
    '& .MuiOutlinedInput-root': {
        borderRadius: 2,
        bgcolor: 'background.paper'
    }
};

const submitButtonSx = {
    mt: 1.5,
    py: 1.3,
    borderRadius: 2,
    fontWeight: 700,
    color: 'common.white',
    background: 'linear-gradient(90deg, #6366F1 0%, #22D3EE 100%)',
    boxShadow: '0 10px 24px rgba(99,102,241,0.35)',
    '&:hover': {
        filter: 'brightness(0.96)',
        boxShadow: '0 10px 28px rgba(34,211,238,0.35)'
    }
};

const Login = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPass, setShowPass] = useState(false);
    const [loading, setLoading] = useState(false);
    const [ssoLoading, setSsoLoading] = useState(false);
    const navigate = useNavigate();
    const LAST_ROUTE_KEY = 'complisense:lastRoute';

    const isSubmitDisabled = useMemo(
        () => !email || !password || loading,
        [email, password, loading]
    );

    useEffect(() => {
        document.title = 'Login | CompliSense';
    }, []);

    useEffect(() => {
        const status = new URLSearchParams(window.location.search).get('sso');
        if (!status) return;

        if (status === 'failed') {
            toast.error('SSO login failed. Please try again.');
        } else if (status === 'user_not_found') {
            toast.error('SSO succeeded, but this account is not mapped in CompliSense.');
        } else if (status === 'not_configured') {
            toast.error('SSO is not configured yet on the server.');
        }
    }, []);

    useEffect(() => {
        let isMounted = true;

        const checkSession = async () => {
            try {
                const { data } = await api.get('/auth/session_data', {
                    withCredentials: true,
                });

                if (!isMounted) return;

                if (data) {
                    const lastRoute =
                        localStorage.getItem(LAST_ROUTE_KEY) || '/dashboard';

                    navigate(lastRoute, { replace: true });
                }
            } catch (err) {
            }
        };

        checkSession();

        return () => {
            isMounted = false;
        };
    }, [navigate]);


    const handleSubmit = useCallback(
        async e => {
            e.preventDefault();
            setLoading(true);
            try {
                const { data: loginData } = await api.post(
                    '/auth/login',
                    { email, password },
                    { withCredentials: true }
                );

                toast.success('Logged in successfully');


                const lastRoute =
                    localStorage.getItem(LAST_ROUTE_KEY) || '/dashboard';

                navigate(lastRoute, { replace: true });
            } catch (err) {
                console.error(err);
                toast.error(
                    err.response?.status === 401
                        ? 'Invalid credentials'
                        : 'Network error, please try again'
                );
            } finally {
                setLoading(false);
            }
        },
        [email, password, navigate]
    );


    const togglePasswordVisibility = useCallback(() => {
        setShowPass(prev => !prev);
    }, []);

    const handleSsoLogin = useCallback(() => {
        const apiBase = String(process.env.REACT_APP_API_URL || '/api/v1').replace(/\/+$/, '');
        const samlLoginUrl = `${apiBase}/auth/saml/login`.replace(/([^:]\/)\/+/g, '$1');
        setSsoLoading(true);
        window.location.assign(samlLoginUrl);
    }, []);

    return (
        <>
            <ToastContainer position="top-center" autoClose={4000} />

            <Box sx={rootBackgroundSx}>
                <Container maxWidth="lg">
                    <Grid
                        container
                        spacing={3}
                        alignItems="stretch"
                        sx={{ minHeight: { md: 620, xs: 'auto' } }}
                    >
                        {/* Hero / Brand panel */}
                        <Grid item xs={12} md={6} sx={{ display: { xs: 'none', md: 'block' } }}>
                            <Paper elevation={0} sx={heroPaperSx}>
                                <Box sx={heroOverlaySx} />
                                <Box sx={{ position: 'relative' }}>
                                    <Box sx={{ mb: 3 }}>
                                        <ComplisenseLogo
                                            variant="wordmark"
                                            size="large"
                                            iconSize={52}
                                            typographySx={{ color: 'rgba(255,255,255,0.98)', fontSize: '1.75rem' }}
                                        />
                                    </Box>
                                    <Typography variant="h5" fontWeight={700} gutterBottom sx={{ opacity: 0.95 }}>
                                        Welcome back
                                    </Typography>
                                    <Typography variant="h6" sx={{ opacity: 0.9, mb: 6 }}>
                                        Streamlined compliance. Secure access. Get things done faster.
                                    </Typography>

                                    <Paper
                                        elevation={0}
                                        sx={{
                                            p: 3,
                                            borderRadius: 3,
                                            bgcolor: 'rgba(255,255,255,0.14)',
                                            border: '1px solid rgba(255,255,255,0.35)',
                                            backdropFilter: 'blur(6px)',
                                            maxWidth: 420
                                        }}
                                    >
                                        <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                                            Why teams choose us
                                        </Typography>
                                        <Typography variant="body2" sx={{ opacity: 0.95 }}>
                                            • Centralized tasks & tracking
                                            <br />
                                            • Granular ownership & reviews
                                            <br />
                                            • Real-time visibility & exports
                                        </Typography>
                                    </Paper>
                                </Box>
                            </Paper>
                        </Grid>

                        {/* Sign-in card */}
                        <Grid item xs={12} md={6}>
                            <Paper elevation={8} sx={authPaperSx}>
                                <Box sx={{ mb: 3, textAlign: { xs: 'center', md: 'left' } }}>
                                    <Box sx={{ display: { xs: 'flex', md: 'none' }, justifyContent: 'center', mb: 2 }}>
                                        <ComplisenseLogo
                                            variant="wordmark"
                                            size="small"
                                            iconSize={36}
                                            typographySx={{ color: 'primary.main', fontSize: '1.25rem' }}
                                        />
                                    </Box>
                                    <Typography variant="h4" fontWeight={800} gutterBottom>
                                        Sign in
                                    </Typography>
                                    <Typography color="text.secondary">
                                        Enter your credentials to access your dashboard.
                                    </Typography>
                                </Box>

                                <Box
                                    component="form"
                                    onSubmit={handleSubmit}
                                    noValidate
                                    aria-busy={loading ? 'true' : 'false'}
                                >
                                    <TextField
                                        fullWidth
                                        label="Email"
                                        type="email"
                                        autoComplete="off"
                                        inputProps={{
                                            autoComplete: "new-email"  // prevents Chrome autofill
                                        }}
                                        autoFocus
                                        margin="normal"
                                        value={email}
                                        onChange={e => setEmail(e.target.value)}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <EmailOutlinedIcon />
                                                </InputAdornment>
                                            )
                                        }}
                                        sx={textFieldSx}
                                    />


                                    <TextField
                                        fullWidth
                                        label="Password"
                                        type={showPass ? "text" : "password"}
                                        autoComplete="new-password"
                                        inputProps={{
                                            autoComplete: "new-password"
                                        }}
                                        margin="normal"
                                        value={password}
                                        onChange={e => setPassword(e.target.value)}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <LockOutlinedIcon />
                                                </InputAdornment>
                                            ),
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton
                                                        onClick={togglePasswordVisibility}
                                                        onMouseDown={e => e.preventDefault()}
                                                        edge="end"
                                                    >
                                                        {showPass ? <VisibilityOff /> : <Visibility />}
                                                    </IconButton>
                                                </InputAdornment>
                                            )
                                        }}
                                        sx={textFieldSx}
                                    />


                                    {/* Bottom row: only Forgot Password now */}
                                    <Box
                                        sx={{
                                            mt: 1,
                                            mb: 2,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'flex-end'
                                        }}
                                    >
                                        <Link
                                            component={RouterLink}
                                            to="/forgot-password"
                                            underline="hover"
                                            sx={{ fontWeight: 600 }}
                                        >
                                            Forgot password?
                                        </Link>
                                    </Box>

                                    <Button
                                        fullWidth
                                        type="submit"
                                        size="large"
                                        disabled={isSubmitDisabled}
                                        sx={submitButtonSx}
                                    >
                                        {loading ? (
                                            <CircularProgress size={24} sx={{ color: 'white' }} />
                                        ) : (
                                            'Login'
                                        )}
                                    </Button>

                                    <Divider sx={{ my: 3 }} />

                                    <Button
                                        fullWidth
                                        type="button"
                                        variant="outlined"
                                        size="large"
                                        disabled={loading || ssoLoading}
                                        onClick={handleSsoLogin}
                                        sx={{ borderRadius: 2, py: 1.2, fontWeight: 700, mb: 2 }}
                                    >
                                        {ssoLoading ? (
                                            <CircularProgress size={22} />
                                        ) : (
                                            'Login with SSO'
                                        )}
                                    </Button>

                                    <Typography variant="body2" color="text.secondary" align="center">
                                        Welcome back. Sign in to continue your compliance tasks.
                                    </Typography>
                                </Box>
                            </Paper>
                        </Grid>
                    </Grid>
                </Container>
            </Box>
        </>
    );
};

export default Login;
